import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useLoginSsoVerifyMutation } from "../api/network/authApiService";
import { RESPONSE_CODE } from "../api/network/constants";
import { serializeErrorKeyValues } from "../api/network/errorCodes";
import {
  setAccessToken,
  setListingQueryParams,
  setPreLoginUserData,
  setUserAuthInProgress,
  setUserAuthType,
  setUserData,
  setUserIsImpersonated,
  TListingQueryParams,
  TUserAuthType,
} from "../api/store/commonSlice";
import {
  LegacyVerifyEmailOtpResponse,
  LegacyVerifyEmailOtpResponseSuccess,
  LoginResponse,
  LoginResponseSuccess,
} from "../api/types/Onboarding";
import { TLegacyUserWithAccount } from "../api/types/User";
import { resetAuthErrorFlag } from "../api/utils/utils";
import { navigateTo } from "../navigation/navigationService";
import { routeUrls } from "../navigation/routeUrls";
import { ROLE_ACCESS_LEVELS } from "../pages/admin-details/role/constants";
import { localStorageKeys, useLocalStorage } from "../utils/localStorageItems";
import { useLoggedInUserPerms } from "../utils/user";
import { ACL_NAMES, useACL } from "./acl";

type TGetPostLoginUrlProps = {
  user: TLegacyUserWithAccount;
};

type TUseLoginHandlerProps = {
  screen: string;
};

export const useLoginHandler = ({ screen }: TUseLoginHandlerProps) => {
  const { t } = useTranslation("translation", { keyPrefix: screen });
  // as this useLoginHandler hook needs to be used at App.tsx to facilitate Azure AD based authentication,
  // and the useNavigate hook was causing issues when being called outside <RouterProvider />,
  // instead, we use an imperative form of navigation via navigateTo, that is defined under navigationService.ts
  // and initialized in navigation/rootLayout.tsx
  // const navigate = useNavigate();
  const dispatch = useDispatch();
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { getLocalStorageItem, setLocalStorageItem } = useLocalStorage();
  const { loggedInUserIsSysAdmin } = useLoggedInUserPerms();
  const { getUserACLAccessLevel } = useACL();

  const [loginSsoVerifyApiTrigger] = useLoginSsoVerifyMutation();

  const { instance: msalInstance } = useMsal();

  const getPostLoginUrl = ({ user }: TGetPostLoginUrlProps) => {
    const { firstloginpageid: pageid } = user;
    // return to saved url if session expired
    const pageBeforeSessionExpire = localStorage.getItem(
      localStorageKeys.pageBeforeSessionExpire
    );
    if (pageBeforeSessionExpire) {
      localStorage.removeItem(localStorageKeys.pageBeforeSessionExpire);
      return pageBeforeSessionExpire;
    }
    // otherwise navigate to the page based on the pageid
    const dashboardUrl = loggedInUserIsSysAdmin()
      ? routeUrls.sysadmin
      : routeUrls.dashboard;
    switch (true) {
      case pageid === "menu.top" || pageid === "menu.dashDetail":
        return dashboardUrl;
      case (pageid === "map.device" || pageid === "map.fleet") &&
        getUserACLAccessLevel(ACL_NAMES.ACL_MAP_FLEET) >=
          ROLE_ACCESS_LEVELS.READ:
        return routeUrls.dashboardChildren.maps;
      case pageid === "menu.rpt.devDetail" || pageid === "menu.rpt.grpSummary":
        return routeUrls.dashboardChildren.reports;
      default:
        return dashboardUrl;
    }
  };

  const prepareSwitchSysadminToAccountUser = () => {
    // retrieve sysadmin data from session storage
    const data: LegacyVerifyEmailOtpResponseSuccess = getLocalStorageItem(
      localStorageKeys.loggedInUserData
    );
    const user_auth_type: string = getLocalStorageItem(
      localStorageKeys.user_auth_type
    );

    // backup sa_ to sa2_ if nested impersonation
    const existingSaData = getLocalStorageItem(
      `sa_${localStorageKeys.loggedInUserData}`
    );
    if (existingSaData) {
      setLocalStorageItem(
        `sa2_${localStorageKeys.loggedInUserData}`,
        existingSaData
      );
      setLocalStorageItem(`sa2_${localStorageKeys.user}`, existingSaData.user);
      setLocalStorageItem(
        `sa2_${localStorageKeys.accessToken}`,
        existingSaData.access_token
      );
      setLocalStorageItem(
        `sa2_${localStorageKeys.ownerOrganization}`,
        existingSaData.user.account
      );
    }

    // moving regular (sysadmin) data to sysadmin keys
    setLocalStorageItem(`sa_${localStorageKeys.loggedInUserData}`, data);
    setLocalStorageItem(`sa_${localStorageKeys.user}`, data.user);
    setLocalStorageItem(
      `sa_${localStorageKeys.user_auth_type}`,
      user_auth_type
    );
    setLocalStorageItem(
      `sa_${localStorageKeys.user_profile}`,
      data.user_profile
    );
    setLocalStorageItem(
      `sa_${localStorageKeys.accessToken}`,
      data.access_token
    );
    setLocalStorageItem(
      `sa_${localStorageKeys.ownerOrganization}`,
      data.user.account
    );
    // moving azure values if they exist
    const azureIdToken = getLocalStorageItem(localStorageKeys.azureIdToken);
    if (!!azureIdToken) {
      setLocalStorageItem(`sa_${localStorageKeys.azureIdToken}`, azureIdToken);
    }
    const azureTokenExpiry = getLocalStorageItem(
      localStorageKeys.azureTokenExpiry
    );
    if (!!azureTokenExpiry) {
      setLocalStorageItem(
        `sa_${localStorageKeys.azureTokenExpiry}`,
        azureTokenExpiry
      );
    }

    // set user is impersonated
    dispatch(setUserIsImpersonated(true));

    // user data to store is set in `onUserTokenRetrievedSetupSessionNRedirect` which is called right after this function
  };

  const prepareSwitchAccountUserToSysadmin = () => {
    const sa2Data = getLocalStorageItem(
      `sa2_${localStorageKeys.loggedInUserData}`
    );
    // retrieve sysadmin data from session storage
    const data: LegacyVerifyEmailOtpResponseSuccess = getLocalStorageItem(
      `sa_${localStorageKeys.loggedInUserData}`
    );
    const user_auth_type: string = getLocalStorageItem(
      `sa_${localStorageKeys.user_auth_type}`
    );

    // moving regular (sysadmin) data to sysadmin keys
    setLocalStorageItem(localStorageKeys.loggedInUserData, data);
    setLocalStorageItem(localStorageKeys.user, data.user);
    setLocalStorageItem(localStorageKeys.user_auth_type, user_auth_type);
    setLocalStorageItem(localStorageKeys.user_profile, data.user_profile);
    setLocalStorageItem(localStorageKeys.accessToken, data.access_token);
    setLocalStorageItem(localStorageKeys.ownerOrganization, data.user.account);
    if (!!data.user?.config?.VISIONMAX_BASE_URL) {
      setLocalStorageItem(
        localStorageKeys.camerasAdminVMAPIURLBase,
        data.user.config.VISIONMAX_BASE_URL
      );
    }
    if (!!data.user?.config?.VISIONMAX_X_API_KEY) {
      setLocalStorageItem(
        localStorageKeys.camerasAdminVMAPIKey,
        data.user.config.VISIONMAX_X_API_KEY
      );
    }
    if (!!data.user?.config?.SUMMARY_PROCESS_COMPLETE_TIME_UTC) {
      setLocalStorageItem(
        localStorageKeys.summaryProcessCompleteTimeUtc,
        data.user.config.SUMMARY_PROCESS_COMPLETE_TIME_UTC
      );
    }
    // moving azure values if they exist
    const azureIdToken = getLocalStorageItem(
      `sa_${localStorageKeys.azureIdToken}`
    );
    if (!!azureIdToken) {
      setLocalStorageItem(localStorageKeys.azureIdToken, azureIdToken);
    }
    const azureTokenExpiry = getLocalStorageItem(
      `sa_${localStorageKeys.azureTokenExpiry}`
    );
    if (!!azureTokenExpiry) {
      setLocalStorageItem(localStorageKeys.azureTokenExpiry, azureTokenExpiry);
    }

    if (sa2Data) {
      // Capture user1's token BEFORE sa_accessToken is overwritten below
      const restoredAccessToken =
        (getLocalStorageItem(`sa_${localStorageKeys.accessToken}`) as string) ||
        data.access_token;

      // restore sa2_ back to sa_
      setLocalStorageItem(`sa_${localStorageKeys.loggedInUserData}`, sa2Data);
      setLocalStorageItem(`sa_${localStorageKeys.user}`, sa2Data.user);
      setLocalStorageItem(
        `sa_${localStorageKeys.accessToken}`,
        sa2Data.access_token
      );
      setLocalStorageItem(
        `sa_${localStorageKeys.ownerOrganization}`,
        sa2Data.user.account
      );

      // cleanup sa2_
      localStorage.removeItem(`sa2_${localStorageKeys.loggedInUserData}`);
      localStorage.removeItem(`sa2_${localStorageKeys.user}`);
      localStorage.removeItem(`sa2_${localStorageKeys.accessToken}`);
      localStorage.removeItem(`sa2_${localStorageKeys.ownerOrganization}`);

      // keep redux state aligned with restored (one-level-up impersonated) user
      dispatch(setUserData(data));
      dispatch(setAccessToken(restoredAccessToken));
      dispatch(setUserIsImpersonated(true));
    } else {
      // Capture sysadmin token BEFORE sa_accessToken is removed in cleanup below
      const restoredAccessToken =
        (getLocalStorageItem(`sa_${localStorageKeys.accessToken}`) as string) ||
        data.access_token;

      // only one level impersonation
      dispatch(setUserIsImpersonated(false));
      dispatch(setUserData(data));
      dispatch(setAccessToken(restoredAccessToken));

      // cleanup sa_
      localStorage.removeItem(`sa_${localStorageKeys.loggedInUserData}`);
      localStorage.removeItem(`sa_${localStorageKeys.user}`);
      localStorage.removeItem(`sa_${localStorageKeys.user_auth_type}`);
      localStorage.removeItem(`sa_${localStorageKeys.user_profile}`);
      localStorage.removeItem(`sa_${localStorageKeys.accessToken}`);
      localStorage.removeItem(`sa_${localStorageKeys.ownerOrganization}`);
      localStorage.removeItem(
        `sa_${localStorageKeys.camerasAdminVMAPIURLBase}`
      );
      localStorage.removeItem(`sa_${localStorageKeys.camerasAdminVMAPIKey}`);
      localStorage.removeItem(
        `sa_${localStorageKeys.summaryProcessCompleteTimeUtc}`
      );
    }
  };

  /**
   * To be called after user token is retrieved
   * Sets up user session and redirects to post login page */
  const onUserTokenRetrievedSetupSessionNRedirect = (
    data: LegacyVerifyEmailOtpResponseSuccess,
    userAuthType: TUserAuthType = "pyDRF"
  ) => {
    dispatch(setUserData(data));
    dispatch(setAccessToken(data.access_token));
    dispatch(setUserAuthType(userAuthType));
    setLocalStorageItem(localStorageKeys.loggedInUserData, data);
    setLocalStorageItem(localStorageKeys.user, data.user);
    setLocalStorageItem(localStorageKeys.user_auth_type, userAuthType);
    setLocalStorageItem(localStorageKeys.user_profile, data.user_profile);
    setLocalStorageItem(localStorageKeys.accessToken, data.access_token);
    setLocalStorageItem(localStorageKeys.ownerOrganization, data.user.account);
    // set camera admin VisionMax variables
    if (!!data.user?.config?.VISIONMAX_BASE_URL)
      setLocalStorageItem(
        localStorageKeys.camerasAdminVMAPIURLBase,
        data.user.config.VISIONMAX_BASE_URL
      );
    if (!!data.user?.config?.VISIONMAX_X_API_KEY)
      setLocalStorageItem(
        localStorageKeys.camerasAdminVMAPIKey,
        data.user.config.VISIONMAX_X_API_KEY
      );
    if (!!data.user?.config?.SUMMARY_PROCESS_COMPLETE_TIME_UTC)
      setLocalStorageItem(
        localStorageKeys.summaryProcessCompleteTimeUtc,
        data.user.config.SUMMARY_PROCESS_COMPLETE_TIME_UTC
      );
    // set listing query params
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        // ensure the keys below match the keys in listingQueryParams
        users: {
          ...listingQueryParams.users,
          accountid: data.user.account.accountid,
        },
        roles: {
          ...listingQueryParams.roles,
          accountid: data.user.account.accountid,
        },
        vehicles: {
          ...listingQueryParams.vehicles,
          accountid: data.user.account.accountid,
        },
        drivers: {
          ...listingQueryParams.drivers,
          accountid: data.user.account.accountid,
        },
        groups: {
          ...listingQueryParams.groups,
          accountid: data.user.account.accountid,
        },
        fleetTags: {
          ...listingQueryParams.fleetTags,
          accountid: data.user.account.accountid,
        },
        geoZones: {
          ...listingQueryParams.geoZones,
          accountid: data.user.account.accountid,
        },
        vehicleAlerts: {
          ...listingQueryParams.vehicleAlerts,
          accountid: data.user.account.accountid,
        },
        vehicleAlertsSingle: {
          ...listingQueryParams.vehicleAlertsSingle,
          accountid: data.user.account.accountid,
        },
        buzzerAlerts: {
          ...listingQueryParams.buzzerAlerts,
          accountid: data.user.account.accountid,
        },
        reports: {
          ...listingQueryParams.reports,
          accountid: data.user.account.accountid,
        },
        rules: {
          ...listingQueryParams.rules,
          accountid: data.user.account.accountid,
        },
        maintenance: {
          ...listingQueryParams.maintenance,
          accountid: data.user.account.accountid,
        },
        documents: {
          ...listingQueryParams.documents,
          accountid: data.user.account.accountid,
        },
        // accountid not needed for roleCategoryACL
        // accountid not needed for sysadminEnterprises
      })
    );
    // reset auth error flag - prevents multiple error popups
    resetAuthErrorFlag();
    // navigate
    navigateTo?.(
      getPostLoginUrl({
        user: (data as LegacyVerifyEmailOtpResponseSuccess).user,
      })
    );
  };

  const loginSuccessHandler = (
    data: LoginResponse | LegacyVerifyEmailOtpResponse,
    userAuthType: TUserAuthType = "pyDRF"
  ) => {
    if (!!(data as LegacyVerifyEmailOtpResponseSuccess)?.access_token) {
      // 2FA is disabled for user / account, and token is received.
      // Redirect to post login page
      onUserTokenRetrievedSetupSessionNRedirect(
        data as LegacyVerifyEmailOtpResponseSuccess,
        userAuthType
      );
      toast.success(t("toast.login_success"));
    } else if ((data as LoginResponseSuccess)?.is_phone) {
      // Both email and phone number are available.
      // Redirect user to select 2fa method selection screen
      dispatch(
        setPreLoginUserData({ email: (data as LoginResponseSuccess)?.email })
      ); // instead of values.email
      // toast.success(t('toast.check_email_phone_for_code')); // , { autoClose: 10000 } // disabled, toast is not needed here
      navigateTo?.(routeUrls.select2FAMethod);
    } else if ((data as LoginResponseSuccess)?.status === 1) {
      // Phone number not available, and Email OTP is sent.
      // Redirect user to email otp verification screen
      dispatch(
        setPreLoginUserData({ email: (data as LoginResponseSuccess)?.email })
      ); // instead of values.email
      toast.success(t("toast.check_email_for_code")); // , { autoClose: 10000 }
      navigateTo?.(`${routeUrls.verify2FA}/email/`);
    } else {
      toast.error(t("toast.login_failed_unknown"));
    }
  };

  const loginErrorHandler = (error: any) => {
    if (
      error?.status !== RESPONSE_CODE.PARSING_ERROR &&
      Number(error?.status) !== RESPONSE_CODE.BAD_REQUEST
    ) {
      const errors = !!error?.data
        ? serializeErrorKeyValues(error?.data)
        : [t("toast.login_failed")];
      toast.error(errors?.join(" "));
    } else if (error?.status === RESPONSE_CODE.BAD_REQUEST) {
      // The server explains when the credentials belong to a child account and the
      // parent account login must be used instead.
      const useParentAccountMessage = error?.data?.user_info_details
        ?.parent_account
        ? error?.data?.msg
        : null;
      toast.error(useParentAccountMessage || t("toast.authentication_failed"));
    } else {
      toast.error(t("toast.login_failed_unknown"));
    }
  };

  // email should not be sent to backend, instead backend should identify user based on the azureIdToken alone. This is because email can be different based on the type of Azure account (personal vs organizational) and can cause issues in user identification and login if sent to backend. --- IGNORE ---
  const consumeAzureTokenForLogin = (
    azureIdToken: string /* , email?: string */
  ) => {
    // set user auth in progress unless `exchangeAzureTokenWithBackend` settles
    dispatch(setUserAuthInProgress(true));

    // call the API to exchange Azure token with backend and login
    loginSsoVerifyApiTrigger({
      idToken: azureIdToken,
    })
      .unwrap()
      .then((response) => loginSuccessHandler(response, "azureAD"))
      .catch(loginErrorHandler)
      .finally(() => {
        dispatch(setUserAuthInProgress(false));
      });
  };

  function handleAzureAuthError(error: any) {
    let message = "Unable to sign in with Company Portal.";

    if (error.errorCode === "access_denied") {
      message = "Sign-in was cancelled.";
    }

    if (error.errorCode === "unauthorized_client") {
      message = "Your account is not authorized to access this application.";
    }

    if (error instanceof InteractionRequiredAuthError) {
      message = "Additional sign-in steps are required. Please try again.";
    }

    if (error.errorMessage?.includes("AADSTS")) {
      // optional logging only
      console.error("Azure AD error:", error.errorMessage);
    }

    toast.error(message);
  }

  function clearAzureBeforeLogout() {
    msalInstance.logout();
  }

  return {
    onUserTokenRetrievedSetupSessionNRedirect,
    loginSuccessHandler,
    loginErrorHandler,
    prepareSwitchSysadminToAccountUser,
    prepareSwitchAccountUserToSysadmin,
    consumeAzureTokenForLogin,
    handleAzureAuthError,
    clearAzureBeforeLogout,
  };
};
